import React, { useState, useEffect } from 'react';
import Lobby from './Lobby';
import ScoreTracker from './ScoreTracker';
import RoleCard from './RoleCard';
import VotePanel from './VotePanel';
import QuestPanel from './QuestPanel';

export default function PlayerView({
  emit, gameState, playerId, roomCode,
  roleInfo, voteResults, questResult, gameOverInfo,
  clearVoteResults, clearQuestResult, onLeave, playerName,
}) {
  const [hasVoted, setHasVoted] = useState(false);
  const [hasPlayedQuest, setHasPlayedQuest] = useState(false);

  // Reset vote/quest status when phase changes
  useEffect(() => {
    if (gameState?.phase === 'voting') {
      setHasVoted(false);
    }
    if (gameState?.phase === 'quest') {
      setHasPlayedQuest(false);
    }
    if (gameState?.phase === 'team_building') {
      clearVoteResults();
      clearQuestResult();
    }
  }, [gameState?.phase, clearVoteResults, clearQuestResult]);

  if (!gameState || gameState.phase === 'lobby') {
    return <Lobby gameState={gameState} roomCode={roomCode} playerId={playerId} onLeave={onLeave} />;
  }

  const displayName = playerName || gameState.players?.find(p => p.id === playerId)?.name;

  const phase = gameState.phase;
  const leader = gameState.players.find(p => p.id === gameState.leaderId);
  const teamNames = gameState.proposedTeam.map(
    tid => gameState.players.find(p => p.id === tid)?.name || '?'
  );

  const renderPhaseContent = () => {
    switch (phase) {
      case 'role_reveal':
        return (
          <div className="role-reveal">
            <RoleCard roleInfo={roleInfo} />
            <div className="waiting-state mt-16">
              Waiting for moderator to begin the first quest...
            </div>
          </div>
        );

      case 'team_building':
        return (
          <>
            <div className="phase-banner">
              <div className="phase-label">Team Building</div>
              <div className="phase-detail">
                {leader?.name} is selecting a team of {gameState.teamSize}
              </div>
            </div>
            {gameState.proposedTeam.length > 0 && (
              <div className="card mt-8">
                <div className="card-header">Proposed Team</div>
                {teamNames.map((name, i) => (
                  <div key={i} className="player-item">
                    <span className="player-name">{name}</span>
                  </div>
                ))}
              </div>
            )}
            {gameState.proposedTeam.length === 0 && (
              <div className="waiting-state">
                <div className="waiting-icon">{'\u231B'}</div>
                Waiting for team proposal...
              </div>
            )}
            <RejectionTracker count={gameState.consecutiveRejections} />
          </>
        );

      case 'voting':
        return (
          <>
            <div className="phase-banner">
              <div className="phase-label">Vote on Team</div>
              <div className="phase-detail">
                {leader?.name}'s team: {teamNames.join(', ')}
              </div>
            </div>
            <VotePanel
              emit={emit}
              gameState={gameState}
              playerId={playerId}
              hasVoted={hasVoted}
            />
            <RejectionTracker count={gameState.consecutiveRejections} />
          </>
        );

      case 'vote_result':
        return (
          <>
            <div className="phase-banner">
              <div className="phase-label">Vote Results</div>
            </div>
            {voteResults && (
              <div className="card">
                <div className="card-header" style={{
                  color: voteResults.approved ? 'var(--success)' : 'var(--danger)',
                }}>
                  Team {voteResults.approved ? 'Approved' : 'Rejected'} ({voteResults.approves}-{voteResults.rejects})
                </div>
                <div className="vote-results">
                  {Object.entries(voteResults.voteDetails).map(([pid, { name, vote }]) => (
                    <div key={pid} className="vote-result-item">
                      <span>{name}</span>
                      <span className={`vote-badge ${vote}`}>
                        {vote === 'approve' ? 'Approve' : 'Reject'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="waiting-state mt-8">
              Waiting for moderator to continue...
            </div>
          </>
        );

      case 'quest':
        return (
          <>
            <div className="phase-banner">
              <div className="phase-label">Quest {gameState.currentQuest + 1}</div>
              <div className="phase-detail">
                Team: {teamNames.join(', ')}
              </div>
            </div>
            <QuestPanel
              emit={emit}
              gameState={gameState}
              playerId={playerId}
              roleInfo={roleInfo}
              hasPlayed={hasPlayedQuest}
            />
          </>
        );

      case 'quest_result':
        return (
          <>
            <div className="phase-banner">
              <div className="phase-label">Quest {gameState.currentQuest + 1} Result</div>
            </div>
            {questResult && (
              <div className="quest-result-display">
                <div className="result-icon">
                  {questResult.questSuccess ? '\u2694' : '\u2620'}
                </div>
                <div className={`result-text ${questResult.questSuccess ? 'success' : 'fail'}`}>
                  Quest {questResult.questSuccess ? 'Succeeded!' : 'Failed!'}
                </div>
                <div className="result-cards">
                  {questResult.successCount} Success / {questResult.failCount} Fail
                </div>
              </div>
            )}
            <div className="waiting-state mt-8">
              Waiting for moderator to continue...
            </div>
          </>
        );

      case 'assassination':
        return (
          <div className="assassination">
            <h3>Assassination Phase</h3>
            <p>
              Good has won 3 quests! The Assassin is now choosing who to assassinate.
              Discuss on Zoom - the moderator will handle the result.
            </p>
          </div>
        );

      case 'game_over':
        return (
          <div className="game-over">
            <div className={`winner-text ${gameOverInfo?.winner || gameState.winner}`}>
              {(gameOverInfo?.winner || gameState.winner) === 'good' ? 'Good Wins!' : 'Evil Wins!'}
            </div>
            <div className="win-reason">
              {gameOverInfo?.reason || gameState.winReason}
            </div>
            <button
              className="btn btn-secondary btn-full mt-16"
              onClick={onLeave}
            >
              Leave
            </button>
          </div>
        );

      default:
        return (
          <div className="waiting-state">
            <div className="waiting-icon">{'\u231B'}</div>
            Waiting...
          </div>
        );
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {displayName && (
        <div className="playing-as-banner">
          Playing as: <strong>{displayName}</strong>
        </div>
      )}
      <ScoreTracker gameState={gameState} />

      {roleInfo && phase !== 'role_reveal' && phase !== 'game_over' && (
        <>
          <RoleCard roleInfo={roleInfo} compact={true} />
          <EvilTeammatesReveal roleInfo={roleInfo} />
        </>
      )}

      {renderPhaseContent()}

      {phase !== 'game_over' && (
        <button
          className="btn btn-secondary btn-full mt-16"
          onClick={() => {
            const msg = phase === 'lobby'
              ? 'Leave this game?'
              : 'Leave the game? You can rejoin later with the same name.';
            if (window.confirm(msg)) {
              onLeave();
            }
          }}
        >
          Leave Game
        </button>
      )}
    </div>
  );
}

function EvilTeammatesReveal({ roleInfo }) {
  const [revealed, setRevealed] = useState(false);

  if (!roleInfo || !roleInfo.knownPlayers || roleInfo.knownPlayers.length === 0) return null;

  return (
    <div className="evil-teammates-reveal">
      <button
        className={`evil-teammates-token ${revealed ? 'open' : ''}`}
        onClick={() => setRevealed(r => !r)}
      >
        <span className="evil-dot" />
        {revealed ? 'Hide Teammates' : 'Reveal Teammates'}
        <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: '0.7rem' }}>
          {revealed ? '▲' : '▼'}
        </span>
      </button>
      {revealed && (
        <div className="evil-teammates-list">
          <div className="known-header" style={{
            fontSize: '0.65rem',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: 6,
          }}>
            {roleInfo.role === 'Merlin' ? 'Evil players revealed to you' :
             roleInfo.role === 'Percival' ? 'Suspects' :
             'Your evil teammates'}
          </div>
          {roleInfo.knownPlayers.map((kp) => (
            <div key={kp.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
              <span className={`known-dot ${kp.label === 'Merlin?' ? 'merlin' : 'evil'}`}
                style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: kp.label === 'Merlin?' ? 'var(--gold)' : 'var(--evil-primary)' }}
              />
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{kp.name}</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                {kp.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RejectionTracker({ count }) {
  return (
    <div style={{ textAlign: 'center', marginTop: '12px' }}>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
        Vote Track
      </div>
      <div className="rejection-tracker">
        {[0, 1, 2, 3, 4].map(i => (
          <div
            key={i}
            className={`rejection-pip ${
              i < count ? (i >= 3 ? 'danger' : 'filled') : ''
            }`}
          />
        ))}
      </div>
      {count >= 4 && (
        <div className="warning-box mt-8">
          One more rejection and Evil wins!
        </div>
      )}
    </div>
  );
}
